import React, { useContext, useEffect, useRef } from 'react';
import {
  Redirect, useHistory, useLocation, useParams,
} from 'react-router-dom';
import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import { Button, Message } from 'semantic-ui-react';
import AiChat from '../components/AiChat';
import Loading from '../components/Loading';
import { CHAT_SESSION_SCOPE } from '../queries/chat';
import { UPDATE_CURRENT_TEAM } from '../queries/profile';
import DataContext from '../contexts/DataContext';
import Security from '../utils/Security';

// The mode lives in the URL (?mode=all) so a refresh keeps it.
const GLOBAL_MODE = 'all';

// Team-wide AI chat. "Team" mode searches every project of the current team.
// Superusers can also switch to "Everything" mode, which searches the whole
// RAG corpus across all teams plus Dexter's expert network.
const AiChatPage = () => {
  const { user, team } = useContext(DataContext);
  const client = useApolloClient();
  const { sessionId } = useParams();
  const location = useLocation();
  const history = useHistory();
  const isSuperUser = Security.isSuperUser();
  const canUseChat = isSuperUser || Security.isDecibio();

  const isGlobal = isSuperUser && new URLSearchParams(location.search).get('mode') === GLOBAL_MODE;
  const modeSearch = isGlobal ? `?mode=${GLOBAL_MODE}` : '';

  // A deep-linked session opens in the mode, and for team sessions the team,
  // it belongs to.
  const { data: scopeData, loading: scopeLoading } = useQuery(CHAT_SESSION_SCOPE, {
    variables: { id: sessionId },
    skip: !sessionId || !canUseChat,
  });
  const sessionScope = scopeData?.chatSession?.scopeType;
  const isModeMismatch = Boolean(sessionScope) && sessionScope !== 'project'
    && (sessionScope === 'global') !== isGlobal;
  useEffect(() => {
    if (isModeMismatch) {
      history.replace(`/ai-chat/${sessionId}${sessionScope === 'global' ? `?mode=${GLOBAL_MODE}` : ''}`);
    }
  }, [isModeMismatch, sessionScope, sessionId, history]);

  // A team session shared from a team other than the current one: switch the
  // current team to it (as the navbar's TeamDropdown does) so the sidebar,
  // scope label and navbar all match the session. resetStore reloads
  // DataContext, which remounts this page with the new team.
  const sessionTeam = sessionScope === 'team' ? scopeData.chatSession.team : null;
  const isOtherTeam = Boolean(sessionTeam) && sessionTeam.id !== team?.id;
  const canSwitchToSessionTeam = isOtherTeam
    && (user.membership || []).some((membership) => membership.teamId === sessionTeam.id);
  const [updateCurrentTeam, { error: switchTeamError }] = useMutation(UPDATE_CURRENT_TEAM);
  const switchingToTeamId = useRef(null);
  useEffect(() => {
    if (!canSwitchToSessionTeam || switchingToTeamId.current === sessionTeam.id) return;
    switchingToTeamId.current = sessionTeam.id;
    updateCurrentTeam({ variables: { teamId: sessionTeam.id } })
      .then(() => client.resetStore())
      .catch(() => {}); // Surfaced via switchTeamError.
  }, [canSwitchToSessionTeam, sessionTeam, updateCurrentTeam, client]);

  // Hold off rendering the chat for a deep link until its scope is known and
  // matches, so it never briefly loads the wrong team's or mode's sessions.
  // Only on first load: sessions picked from the sidebar later already belong
  // to the current scope, and blanking the chat while their scope loads would
  // drop its in-progress state.
  const hasRenderedChat = useRef(false);

  // A superuser can open a team session from a team they aren't a member of
  // (for support) but can't make it their current team, so view it in that
  // team's scope instead. Kept while the next session's scope loads, so moving
  // between that team's sessions doesn't remount the chat.
  const viewedOtherTeam = useRef(null);
  if (!scopeLoading) {
    viewedOtherTeam.current = isOtherTeam && !canSwitchToSessionTeam ? sessionTeam : null;
  }
  const chatTeam = viewedOtherTeam.current || team;

  if (!canUseChat) return <Redirect to="/home" />;

  if (switchTeamError) {
    return (
      <Message negative>
        {`Couldn't switch to ${sessionTeam?.name || 'this chat\'s team'}: ${switchTeamError.message}`}
      </Message>
    );
  }
  const isWaitingForScope = Boolean(sessionId) && scopeLoading && !hasRenderedChat.current;
  if (isWaitingForScope || isModeMismatch || canSwitchToSessionTeam) return <Loading />;
  hasRenderedChat.current = true;

  const switchMode = (global) => {
    if (global === isGlobal) return;
    history.push(`/ai-chat${global ? `?mode=${GLOBAL_MODE}` : ''}`);
  };

  return (
    <div id="ai-chat">
      <AiChat
        // Remount on a mode or team switch so no session or message state leaks across scopes.
        key={isGlobal ? 'global' : `team-${chatTeam.id}`}
        scope={isGlobal ? 'global' : 'team'}
        teamId={chatTeam.id}
        scopeLabel={isGlobal ? 'Searching all teams and the expert network' : `Searching ${chatTeam.name} projects`}
        sessionId={sessionId}
        onSessionChange={(id) => history.push(`/ai-chat${id ? `/${id}` : ''}${modeSearch}`)}
        sidebarHeader={isSuperUser && (
          // Short labels for the narrow column: the chat header's scope label names the team.
          <div style={{ marginBottom: '1em' }}>
            <Button.Group fluid size="small">
              <Button active={!isGlobal} onClick={() => switchMode(false)}>Team</Button>
              <Button active={isGlobal} onClick={() => switchMode(true)}>Everything</Button>
            </Button.Group>
          </div>
        )}
      />
    </div>
  );
};

export default AiChatPage;
